import { ImageSquare, SealWarning } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { getStateFlagAsset } from '@/lib/state-flag-assets';

interface StateFlagImageProps {
  stateCode: string;
  stateName?: string;
  className?: string;
  showSourceBadge?: boolean;
}

export function StateFlagImage({ stateCode, stateName, className = '', showSourceBadge = true }: StateFlagImageProps) {
  const flagAsset = getStateFlagAsset(stateCode);
  const label = stateName ? `${stateName} state flag` : `${stateCode.toUpperCase()} state flag`;
  const isVerified = flagAsset.status === 'verified-official';
  const isWikimediaFallback = flagAsset.status === 'wikimedia-fallback';
  const hasRenderableFlag = Boolean(flagAsset.imagePath) && (isVerified || isWikimediaFallback);
  const code = stateCode.toUpperCase();
  const optimizedImagePath = flagAsset.imagePath
    ?.replace('/flags/official/', '/flags/optimized/')
    .replace('/flags/wiki/', '/flags/optimized/')
    .replace(/\.(?:png|jpe?g)$/i, '.jpg');

  return (
    <div
      className={`relative flex h-full w-full items-center justify-center overflow-hidden bg-[#15231d] ${className}`}
    >
      {hasRenderableFlag && flagAsset.imagePath ? (
        <div className="relative flex h-full w-full items-center justify-center bg-[#f7f2e6]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_0%,rgba(255,255,255,0.72),transparent_36%),linear-gradient(135deg,rgba(23,63,49,0.08),transparent_50%)]" />
          <img
            src={optimizedImagePath || flagAsset.imagePath}
            alt={`${label} from ${flagAsset.sourceLabel}`}
            className="relative h-full w-full object-contain p-3"
            loading="lazy"
            decoding="async"
            onError={(event) => {
              const image = event.currentTarget;
              if (flagAsset.imagePath && image.dataset.sourceFallback !== 'true') {
                image.dataset.sourceFallback = 'true';
                image.src = flagAsset.imagePath;
              }
            }}
          />
        </div>
      ) : (
        <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_20%_10%,rgba(246,202,141,0.24),transparent_30%),linear-gradient(135deg,#172b22,#0f1d18)] px-6 text-center text-white">
          <div className="absolute inset-4 rounded-[1rem] border border-white/10" />
          <div className="absolute -right-12 -top-12 h-36 w-36 rounded-full border border-white/10" />
          <div className="absolute -bottom-12 -left-12 h-32 w-32 rounded-full border border-white/10" />
          <div className="relative grid h-12 w-12 place-items-center rounded-2xl bg-white/10 text-[#f6ca8d] ring-1 ring-white/10">
            {flagAsset.status === 'official-source-blocked' ? (
              <SealWarning size={27} weight="duotone" />
            ) : (
              <ImageSquare size={27} weight="duotone" />
            )}
          </div>
          <div className="relative mt-3">
            <p className="font-serif text-4xl font-black tracking-[-0.08em] text-white/95">{code}</p>
            <p className="mt-1 text-[10px] font-black uppercase tracking-[0.22em] text-[#f6ca8d]">
              Official source pending
            </p>
            <p className="mx-auto mt-2 max-w-[13rem] text-[11px] font-semibold leading-relaxed text-white/62">
              Flag artwork is queued for review for {code}.
            </p>
          </div>
        </div>
      )}

      {showSourceBadge && (
        <Badge
          variant={isVerified ? 'default' : 'secondary'}
          className="absolute bottom-2 right-2 rounded-full bg-white/95 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.14em] text-[#15231d] shadow-sm ring-1 ring-[#173f31]/10"
        >
          {isVerified ? 'Official file' : isWikimediaFallback ? 'Wiki fallback' : 'Source needed'}
        </Badge>
      )}
    </div>
  );
}
